/**
 * useIdleTimeout — universal idle-session-timeout hook for all Flowgenix portals.
 *
 * Detects user inactivity, shows a configurable warning countdown, then calls
 * the provided onTimeout callback (usually logout).  Works identically in
 * learner-app, agent_portal, and every vertical-agent SPA.
 *
 * Usage:
 *   const { isWarning, countdown, extendSession } = useIdleTimeout({
 *     timeoutMs:  30 * 60 * 1000,   // 30 min idle → logout
 *     warningMs:  2  * 60 * 1000,   // show warning 2 min before
 *     onTimeout:  logout,
 *     enabled:    !!user,            // only run while authenticated
 *   });
 *
 * The hook is intentionally side-effect-free before `enabled` is true so it
 * can be called unconditionally in App roots without if-guards.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Activity events that reset the idle timer.
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'focus',
  'visibilitychange',
];

/**
 * @param {object} opts
 * @param {number}   opts.timeoutMs      — ms of inactivity before onTimeout fires (default 30 min)
 * @param {number}   opts.warningMs      — ms before timeout to show the warning modal (default 2 min)
 * @param {function} opts.onTimeout      — called when the session finally times out
 * @param {boolean}  [opts.enabled=true] — set to !!user to disable before login
 * @param {function} [opts.onActivity]   — optional: heartbeat callback on user activity (e.g. PATCH /session/activity)
 *
 * @returns {{ isWarning: boolean, countdown: number, extendSession: function }}
 */
export function useIdleTimeout({
  timeoutMs = 30 * 60 * 1000,
  warningMs = 2 * 60 * 1000,
  onTimeout,
  enabled = true,
  onActivity,
} = {}) {
  const [isWarning, setIsWarning] = useState(false);
  // countdown in seconds while warning is shown
  const [countdown, setCountdown] = useState(Math.floor(warningMs / 1000));

  // Refs avoid stale-closure issues inside event listeners
  const timeoutRef    = useRef(null);
  const warningRef    = useRef(null);
  const countdownRef  = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const onTimeoutRef  = useRef(onTimeout);
  const onActivityRef = useRef(onActivity);

  // Keep callback refs fresh without re-registering all event listeners
  useEffect(() => { onTimeoutRef.current  = onTimeout;  }, [onTimeout]);
  useEffect(() => { onActivityRef.current = onActivity; }, [onActivity]);

  // ── Internal helpers ────────────────────────────────────────────────────────

  const clearAllTimers = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);
  }, []);

  /** Reset the idle clock — called on every activity event and on "Stay logged in". */
  const resetTimer = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();
    setIsWarning(false);
    setCountdown(Math.floor(warningMs / 1000));
    clearAllTimers();

    // Schedule the warning
    warningRef.current = setTimeout(() => {
      setIsWarning(true);
      setCountdown(Math.floor(warningMs / 1000));

      // Countdown ticker (1 s resolution)
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
          }
          return Math.max(0, prev - 1);
        });
      }, 1000);
    }, timeoutMs - warningMs);

    // Schedule the actual logout
    timeoutRef.current = setTimeout(() => {
      clearAllTimers();
      setIsWarning(false);
      onTimeoutRef.current?.();
    }, timeoutMs);
  }, [enabled, timeoutMs, warningMs, clearAllTimers]);

  // ── Activity listener registration ─────────────────────────────────────────

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setIsWarning(false);
      return;
    }

    // Throttle: fire activity handler at most every 10 s to avoid excessive renders
    let lastFire = 0;
    const handleActivity = (e) => {
      // visibilitychange: only reset when tab becomes visible
      if (e.type === 'visibilitychange' && document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (now - lastFire < 10_000) return;
      lastFire = now;

      resetTimer();
      onActivityRef.current?.();
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handleActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  /** "Stay logged in" — resets the idle clock. */
  const extendSession = useCallback(() => {
    resetTimer();
    onActivityRef.current?.();
  }, [resetTimer]);

  return { isWarning, countdown, extendSession };
}

export default useIdleTimeout;
